import { AuthError, AuthErrorType } from '../types/auth';

export interface ErrorHandlerOptions {
  maxRetries?: number;
  retryDelay?: number;
  shouldRetry?: (error: any) => boolean;
}

/**
 * 認証エラーハンドラー
 * Supabaseのエラーを適切なAuthError形式に変換し、ユーザーフレンドリーなメッセージを提供
 */
export class AuthErrorHandler {
  private static readonly DEFAULT_MAX_RETRIES = 3;
  private static readonly DEFAULT_RETRY_DELAY = 1000;

  /**
   * 認証エラーを処理し、適切なAuthError形式に変換
   */
  static handleAuthError(error: any, context?: string): AuthError {
    console.error(`Auth error in ${context || 'unknown context'}:`, error);

    // エラーが既にAuthErrorの場合はそのまま返す
    if (error && typeof error === 'object' && 'type' in error && 'message' in error) {
      return error as AuthError;
    }

    if (!error) {
      return {
        type: AuthErrorType.UNKNOWN_ERROR,
        message: '不明なエラーが発生しました',
      };
    }

    const message = error.message || error.toString() || 'Unknown error occurred';

    // ネットワークエラーの判定
    if (this.isNetworkError(error)) {
      return {
        type: AuthErrorType.NETWORK_ERROR,
        message: 'ネットワークエラーが発生しました。接続を確認してもう一度お試しください',
        details: error,
      };
    }

    // Supabaseエラーメッセージに基づいてエラータイプを判定
    if (message.includes('Invalid login credentials')) {
      return {
        type: AuthErrorType.INVALID_CREDENTIALS,
        message: 'メールアドレスまたはパスワードが正しくありません',
        details: error,
      };
    }

    if (message.includes('Email not confirmed')) {
      return {
        type: AuthErrorType.EMAIL_NOT_CONFIRMED,
        message: 'メールアドレスの確認が完了していません。確認メールをご確認ください',
        details: error,
      };
    }

    if (message.includes('User not found')) {
      return {
        type: AuthErrorType.USER_NOT_FOUND,
        message: 'ユーザーが見つかりません',
        details: error,
      };
    }

    if (message.includes('Password should be at least') || message.includes('weak password')) {
      return {
        type: AuthErrorType.WEAK_PASSWORD,
        message: 'パスワードは8文字以上で設定してください',
        details: error,
      };
    }

    if (
      message.includes('User already registered') ||
      message.includes('already exists') ||
      message.includes('すでに登録済みアカウントです') ||
      message.includes('USER_ALREADY_EXISTS')
    ) {
      return {
        type: AuthErrorType.EMAIL_ALREADY_EXISTS,
        message: 'このメールアドレスは既に登録されています',
        details: error,
      };
    }

    if (message.includes('OAuth') || message.includes('social')) {
      return {
        type: AuthErrorType.SOCIAL_LOGIN_ERROR,
        message: 'ソーシャルログインに失敗しました。もう一度お試しください',
        details: error,
      };
    }

    if (message.includes('account linking') || message.includes('identity')) {
      return {
        type: AuthErrorType.SOCIAL_ACCOUNT_LINKING_ERROR,
        message:
          'アカウントの連携に失敗しました。既存のアカウントと同じメールアドレスの可能性があります',
        details: error,
      };
    }

    // HTTP 405エラーの場合は、ユーザーが既に存在することを意味する可能性が高い
    if (message.includes('HTTP 405')) {
      return {
        type: AuthErrorType.USER_ALREADY_EXISTS,
        message: 'すでに登録済みアカウントです',
        details: error,
      };
    }

    // Supabaseの確認メール送信エラーを検出
    if (
      (error.code === 'unexpected_failure' ||
        error.error_code === 'unexpected_failure' ||
        error.status === 'unexpected_failure') &&
      (message.includes('Error sending confirmation email') ||
        message.includes('confirmation email') ||
        message.includes('email send') ||
        message.includes('mail send'))
    ) {
      return {
        type: AuthErrorType.EMAIL_SEND_FAILURE,
        message: '正しく登録できませんでした。サポートまでお問い合わせください',
        details: error,
      };
    }

    return {
      type: AuthErrorType.UNKNOWN_ERROR,
      message: message,
      details: error,
    };
  }

  /**
   * ネットワークエラーかどうかを判定
   */
  private static isNetworkError(error: any): boolean {
    // ネットワークエラーの判定
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return true;
    }

    if (
      error.message &&
      (error.message.includes('Network Error') ||
        error.message.includes('Failed to fetch') ||
        error.message.includes('Network request failed') ||
        error.message.includes('ERR_NETWORK') ||
        error.message.includes('ERR_INTERNET_DISCONNECTED'))
    ) {
      return true;
    }

    // HTTPステータスコードによる判定
    if (error.status >= 500 || error.status === 0) {
      return true;
    }

    return false;
  }

  /**
   * 指定した時間だけ待機
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * エラーメッセージをユーザーフレンドリーな形式に変換
   */
  static getDisplayMessage(error: AuthError): string {
    switch (error.type) {
      case AuthErrorType.INVALID_CREDENTIALS:
        return 'メールアドレスまたはパスワードが正しくありません';
      case AuthErrorType.EMAIL_NOT_CONFIRMED:
        return 'メールアドレスの確認が完了していません。確認メールをご確認ください';
      case AuthErrorType.USER_NOT_FOUND:
        return 'ユーザーが見つかりません';
      case AuthErrorType.WEAK_PASSWORD:
        return 'パスワードは8文字以上で設定してください';
      case AuthErrorType.EMAIL_ALREADY_EXISTS:
        return 'このメールアドレスは既に登録されています';
      case AuthErrorType.USER_ALREADY_EXISTS:
        return 'すでに登録済みアカウントです\nログインしました';
      case AuthErrorType.SOCIAL_LOGIN_ERROR:
        return 'ソーシャルログインに失敗しました。もう一度お試しください';
      case AuthErrorType.SOCIAL_ACCOUNT_LINKING_ERROR:
        return 'アカウントの連携に失敗しました。既存のアカウントと同じメールアドレスの可能性があります';
      case AuthErrorType.NETWORK_ERROR:
        return 'ネットワークエラーが発生しました。接続を確認してもう一度お試しください';
      case AuthErrorType.EMAIL_SEND_FAILURE:
        return '正しく登録できませんでした。サポートまでお問い合わせください';
      default:
        return error.message || '予期しないエラーが発生しました';
    }
  }

  /**
   * エラーに対する推奨アクションを取得
   */
  static getRecommendedAction(error: AuthError): string | null {
    switch (error.type) {
      case AuthErrorType.EMAIL_NOT_CONFIRMED:
        return '確認メールが届いていない場合は、迷惑メールフォルダをご確認ください';
      case AuthErrorType.WEAK_PASSWORD:
        return '英数字を組み合わせた8文字以上のパスワードを設定してください';
      case AuthErrorType.NETWORK_ERROR:
        return 'インターネット接続を確認して、もう一度お試しください';
      case AuthErrorType.SOCIAL_ACCOUNT_LINKING_ERROR:
        return '既存のアカウントでログインしてから、設定画面でソーシャルアカウントを連携してください';
      case AuthErrorType.EMAIL_SEND_FAILURE:
        return null; // メインメッセージに含まれているため、推奨アクションは不要
      default:
        return null;
    }
  }

  /**
   * エラーが再試行可能かどうかを判定
   */
  static isRetryableError(error: AuthError): boolean {
    switch (error.type) {
      case AuthErrorType.NETWORK_ERROR:
      case AuthErrorType.UNKNOWN_ERROR:
        return true;
      case AuthErrorType.INVALID_CREDENTIALS:
      case AuthErrorType.EMAIL_ALREADY_EXISTS:
      case AuthErrorType.USER_ALREADY_EXISTS:
      case AuthErrorType.WEAK_PASSWORD:
      case AuthErrorType.EMAIL_NOT_CONFIRMED:
      case AuthErrorType.USER_NOT_FOUND:
      case AuthErrorType.SOCIAL_LOGIN_ERROR:
      case AuthErrorType.SOCIAL_ACCOUNT_LINKING_ERROR:
      case AuthErrorType.EMAIL_SEND_FAILURE:
        return false;
      default:
        return true; // 不明なエラータイプの場合は再試行可能とする
    }
  }

  /**
   * リトライ機能付きで操作を実行
   */
  static async executeWithRetry<T>({
    operation,
    errorMessage = 'Operation failed',
    options = {},
  }: {
    operation: () => Promise<T>;
    errorMessage?: string;
    options?: ErrorHandlerOptions;
  }): Promise<T> {
    const {
      maxRetries = this.DEFAULT_MAX_RETRIES,
      retryDelay = this.DEFAULT_RETRY_DELAY,
      shouldRetry,
    } = options;

    let lastError: any;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;
        attempt++;

        // 最後の試行でエラーが発生した場合、またはリトライすべきでないエラーの場合
        if (attempt > maxRetries || (shouldRetry && !shouldRetry(error))) {
          break;
        }

        // リトライ前に待機
        await this.delay(retryDelay * attempt);
      }
    }

    // この行に到達することはないが、TypeScriptの型チェックのため
    throw lastError || new Error('Unknown error occurred');
  }
}

/**
 * 認証操作用のヘルパー関数
 */
export const withErrorHandling = async <T>(
  operation: () => Promise<T>,
  context?: string,
  options?: ErrorHandlerOptions
): Promise<T> => {
  try {
    return await AuthErrorHandler.executeWithRetry({
      operation,
      errorMessage: context,
      options: {
        ...options,
        shouldRetry: (error: any) => {
          // エラーをAuthError形式に変換してから判定
          const authError = AuthErrorHandler.handleAuthError(error, context);
          return AuthErrorHandler.isRetryableError(authError);
        },
      },
    });
  } catch (error) {
    // エラーをAuthError形式に変換してスロー
    throw AuthErrorHandler.handleAuthError(error, context);
  }
};
